import SchoolOfficer from "@/models/APIModels/SchoolOfficer";
import { IApiClient, getApiClient } from "./ApiClient/ApiClient";
import { SecurityResponse } from "./SecurityApi";
import logger, { LoggingSource } from "@/shared/Logger";
import IResponseModel from "./ResponseModel";
import LoggerMasker from "@/shared/Logger/LoggerMasker";
import ChargeDetail from "@/models/APIModels/ChargeDetail";
import PaymentSource from "@/models/APIModels/PaymentSource";
import AdministrationPayer from "@/models/APIModels/AdministrationPayer";
import CMPayment from "@/models/APIModels/CMPayment";

export default class SchoolOfficerApi {
  private readonly _apiClient: IApiClient;
  private readonly URL = "/schoolofficer";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async insert(
    schoolOfficer: SchoolOfficer,
  ): Promise<IResponseModel<SecurityResponse<SchoolOfficer>>> {
    try {
      logger.info("insertSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter SchoolOfficer: ${JSON.stringify(
          LoggerMasker.maskValues(schoolOfficer),
        )}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "insertSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.post<
        SchoolOfficer,
        SecurityResponse<SchoolOfficer>
      >(this.URL, schoolOfficer);
      logger.timeEnd("insertSchoolOfficer call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `insertSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("insertSchoolOfficer call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...insertSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }

  async update(
    schoolOfficer: SchoolOfficer,
    userReference: string,
  ): Promise<IResponseModel<SchoolOfficer>> {
    try {
      logger.info("updateSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter SchoolOfficer: ${JSON.stringify(
          LoggerMasker.maskValues(schoolOfficer),
        )}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "updateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.put<SchoolOfficer, SchoolOfficer>(
        this.URL + `/${userReference}`,
        schoolOfficer,
      );
      logger.timeEnd("updateSchoolOfficer call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `updateSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("updateSchoolOfficer call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...updateSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }

  async getByReference(
    userReference: string,
  ): Promise<IResponseModel<SchoolOfficer>> {
    try {
      logger.info(
        "getByReferenceSchoolOfficer...",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "getByReferenceSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.get<SchoolOfficer>(
        this.URL + `/${userReference}`,
      );
      logger.timeEnd(
        "getByReferenceSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `getByReferenceSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "getByReferenceSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info(
        "...getByReferenceSchoolOfficer",
        LoggingSource.UISchoolOfficer,
      );
    }
  }

  async getAll(): Promise<IResponseModel<SchoolOfficer[]>> {
    try {
      logger.info("getAllSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.timeStart(
        "getAllSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.get<SchoolOfficer[]>(this.URL);
      logger.timeEnd("getAllSchoolOfficer call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `getAllSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getAllSchoolOfficer call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...getAllSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }

  async getBySchool(
    schoolReference: string,
    includeActiveOfficers: boolean,
    includeInactiveOfficers: boolean,
    sortBy?: string,
  ): Promise<IResponseModel<SchoolOfficer[]>> {
    try {
      logger.info("getBySchool...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter SchoolReference: ${schoolReference}, IncludeInactiveOfficers: ${includeInactiveOfficers}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "getAllSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.get<SchoolOfficer[]>(
        this.URL +
          `?schoolreference=${encodeURIComponent(schoolReference)}&includeActiveOfficers=${encodeURIComponent(includeActiveOfficers)}&includeInactiveOfficers=${encodeURIComponent(includeInactiveOfficers)}${sortBy}`,
      );
      logger.timeEnd("getAllSchoolOfficer call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `getBySchool response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getAllSchoolOfficer call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...getBySchool", LoggingSource.UISchoolOfficer);
    }
  }

  async requestBankAccountChangeSMSCode(
    userReference: string,
  ): Promise<IResponseModel<void>> {
    try {
      logger.info(
        "requestBankAccountChangeSMSCode...",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "requestBankAccountChangeSMSCode call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.get<void>(
        this.URL +
          `/${userReference}/requestbankaccountchangeviasmscodechallenge`,
      );
      logger.timeEnd(
        "requestBankAccountChangeSMSCode call",
        LoggingSource.UISchoolOfficer,
      );
      logger.debug(
        `requestBankAccountChangeSMSCode response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "requestBankAccountChangeSMSCode call",
        LoggingSource.UISchoolOfficer,
      );
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info(
        "...requestBankAccountChangeSMSCode",
        LoggingSource.UISchoolOfficer,
      );
    }
  }

  async authoriseBankAccountChange(
    userReference: string,
    verificationCode: string,
  ): Promise<IResponseModel<SecurityResponse<SchoolOfficer>>> {
    try {
      logger.info(
        "authoriseBankAccountChange...",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `Parameter UserReference: ${userReference}, VerrificationCode: ${verificationCode}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "authoriseBankAccountChange call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.get<SecurityResponse<SchoolOfficer>>(
        this.URL +
          `/${userReference}/authoriseBankAccountChange/${verificationCode}`,
      );
      logger.timeEnd(
        "authoriseBankAccountChange call",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `authoriseBankAccountChange response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "authoriseBankAccountChange call",
        LoggingSource.UISchoolOfficer,
      );
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info(
        "...authoriseBankAccountChange",
        LoggingSource.UISchoolOfficer,
      );
    }
  }

  async establishAccess(userReference: string): Promise<IResponseModel<void>> {
    try {
      logger.info("establishAccess...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart("establishAccess call", LoggingSource.UISchoolOfficer);
      const result = await this._apiClient.put<string, void>(
        this.URL + `/${userReference}/establishAccess`,
      );
      logger.timeEnd("establishAccess call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `establishAccess response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("establishAccess call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...establishAccess", LoggingSource.UISchoolOfficer);
    }
  }

  async resendEmail(userReference: string): Promise<IResponseModel<void>> {
    try {
      logger.info("resendEmail...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart("resendEmail call", LoggingSource.UISchoolOfficer);
      const result = await this._apiClient.put<string, void>(
        this.URL +
          `/resendemail?userReference=${encodeURIComponent(userReference)}`,
      );
      logger.timeEnd("resendEmail call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `resendEmail response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("resendEmail call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...resendEmail", LoggingSource.UISchoolOfficer);
    }
  }

  async activateSchoolOfficer(
    userReference: string,
    concurrency: string,
  ): Promise<IResponseModel<SchoolOfficer>> {
    try {
      logger.info("activateSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "activateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.patch<SchoolOfficer>(
        `${
          this.URL
        }/${userReference}/activate?concurrencyRv=${encodeURIComponent(
          concurrency,
        )}`,
      );
      logger.timeEnd(
        "activateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `activateSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "activateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...activateSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }

  async deactivateSchoolOfficer(userReference: string, concurrency: string) {
    try {
      logger.info("deactivateSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "deactivateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.patch<SchoolOfficer>(
        `${
          this.URL
        }/${userReference}/deactivate?concurrencyRv=${encodeURIComponent(
          concurrency,
        )}`,
      );
      logger.timeEnd(
        "deactivateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );

      logger.debug(
        `deactivateSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "deactivateSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...deactivateSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }

  async setPaymentSource(
    paymentSource: PaymentSource,
    schoolOfficerReference: string,
    concurrencyRV: string,
  ): Promise<IResponseModel<AdministrationPayer>> {
    try {
      logger.info("setPaymentSource...", LoggingSource.UIUser);

      logger.debug(
        `Parameter PaymentSource: ${JSON.stringify(
          paymentSource,
        )}, School Officer Reference: ${schoolOfficerReference}, ConcurrencyRV: ${concurrencyRV}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("setPaymentSource call", LoggingSource.UIUser);
      const result = await this._apiClient.put<
        { paymentSource: PaymentSource; concurrencyRV: string },
        AdministrationPayer
      >(`${this.URL}/${schoolOfficerReference}/paymentsource`, {
        paymentSource,
        concurrencyRV,
      });
      logger.timeEnd("setPaymentSource call", LoggingSource.UIUser);

      logger.debug(
        `setPaymentSource response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("setPaymentSource call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...setPaymentSource", LoggingSource.UIUser);
    }
  }

  async chargeAccount(
    userReference: string,
  ): Promise<IResponseModel<ChargeDetail>> {
    try {
      logger.info("ChargeAccount...", LoggingSource.UIUser);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("ChargeAccount call", LoggingSource.UIUser);
      const result = await this._apiClient.get<ChargeDetail>(
        `${this.URL}/${userReference}}/chargeaccount`,
      );
      logger.timeEnd("ChargeAccount call", LoggingSource.UIUser);

      logger.debug(
        `ChargeAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("ChargeAccount call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...ChargeAccount", LoggingSource.UIUser);
    }
  }

  async getPaymentList(queryString: string) {
    try {
      logger.info("getList...", LoggingSource.UIDonorPayment);

      logger.timeStart("getList call", LoggingSource.UIDonorPayment);

      const result = await this._apiClient.get<CMPayment[]>(
        `${this.URL}/paymentlist?${queryString}`,
      );

      logger.timeEnd("getList call", LoggingSource.UIDonorPayment);

      logger.debug(
        `getList response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getList call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getList", LoggingSource.UIDonorPayment);
    }
  }

  async deleteSchoolOfficer(
    userReference: string,
    concurrencyRV: string,
  ): Promise<IResponseModel<void>> {
    try {
      logger.info("deleteSchoolOfficer...", LoggingSource.UISchoolOfficer);

      logger.debug(
        `Parameter UserReference: ${userReference}, ConcurrencyRV: ${concurrencyRV}`,
        LoggingSource.UISchoolOfficer,
      );

      logger.timeStart(
        "deleteSchoolOfficer call",
        LoggingSource.UISchoolOfficer,
      );
      const result = await this._apiClient.delete<void>(
        `${this.URL}/${userReference}?concurrencyRV=${encodeURIComponent(concurrencyRV)}`,
      );
      logger.timeEnd("deleteSchoolOfficer call", LoggingSource.UISchoolOfficer);

      logger.debug(
        `deleteSchoolOfficer response: ${JSON.stringify(result)}`,
        LoggingSource.UISchoolOfficer,
      );
      return result;
    } catch (error) {
      logger.timeEnd("deleteSchoolOfficer call", LoggingSource.UISchoolOfficer);
      logger.error(JSON.stringify(error), LoggingSource.UISchoolOfficer);

      throw error;
    } finally {
      logger.info("...deleteSchoolOfficer", LoggingSource.UISchoolOfficer);
    }
  }
}
